import { useState, useCallback, useRef } from "react";
import { useAppSelector } from "@/store/hooks";
import { debounce } from "@/utils/debounce";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listProductInfos } from "@/graphql/queries";
import { getDefaultParseVariants } from "./useCustomPaginatedProducts";
import { sortProductByName } from "@/utils/helper/sortProductByName";

interface UseSmartProductSearchOptions {
  debounceMs?: number;
}

export function useSmartProductSearch({ debounceMs = 400 }: UseSmartProductSearchOptions = {}) {
  const brands = useAppSelector((state) => state.app.brands);
  const categories = useAppSelector((state) => state.app.categories);
  const [input, setInput] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const debouncedSearchRef = useRef(
    debounce(async (searchValue: string) => {
      setError(null);
      setResults([]);
      setLoading(true);
      try {
        const productRes = await executeGraphQLMutation({
          query: listProductInfos,
          variables: {
            filter: {
              name: { contains: searchValue?.slice(0, 1)?.toUpperCase() + searchValue?.slice(1) },
              is_active: { eq: true },
              is_deleted: { eq: false },
            },
          },
          showToast: false,
        });
        if (productRes && productRes.items && productRes.items.length > 0) {
          setResults(sortProductByName(getDefaultParseVariants(productRes.items)));
          setLoading(false);
          return;
        }
        const foundBrand = brands.find((b) => b?.name?.toLowerCase() === searchValue.toLowerCase());
        if (foundBrand) {
          const brandProductRes = await executeGraphQLMutation({
            query: listProductInfos,
            variables: {
              filter: {
                brand_id: { eq: foundBrand.id },
                is_active: { eq: true },
                is_deleted: { eq: false },
              },
            },
            showToast: false,
          });
          if (brandProductRes?.items && brandProductRes.items.length > 0) {
            setResults(sortProductByName(getDefaultParseVariants(brandProductRes.items)));
            setLoading(false);
            return;
          }
        }
        const foundCategory = categories.find(
          (cat) => cat?.name?.toLowerCase() === searchValue.toLowerCase()
        );
        if (foundCategory) {
          const categoryProductRes = await executeGraphQLMutation({
            query: listProductInfos,
            variables: {
              filter: {
                category_id: { contains: foundCategory.id },
                is_active: { eq: true },
                is_deleted: { eq: false },
              },
            },
            showToast: false,
          });
          setResults(sortProductByName(getDefaultParseVariants(categoryProductRes?.items || [])));
        } else {
          setResults([]);
        }
      } catch (err: any) {
        setError(err.message || "Error searching products");
      } finally {
        setLoading(false);
      }
    }, debounceMs)
  );

  const onInputChange = useCallback(
    (val: string) => {
      setInput(val);
      if (val.trim()) {
        debouncedSearchRef.current(val.trim());
      } else {
        setResults([]);
      }
    },
    [brands, categories]
  );

  return {
    input,
    setInput: onInputChange,
    results,
    loading,
    error,
  };
}
